import React, { FC, useRef, useCallback } from "react";
import { useField } from "formik";
import classNames from "classnames";
import { Tag } from "../Tag/Tag";
import { Button } from "../Button/Button";
import { CloseIcon } from "../../../icons";
import { useInteraction } from "../../../hooks/useInteraction";
import { InputSize } from "../../../helpers/form";
import styles from "./TagsInput.module.scss";

const DEFAULT_SEPARATORS = ["Enter", " ", ","];

export interface TagsInputProps {
    name: string;
    separators?: string[];
    placeholder?: string;
    size?: InputSize;
    className?: string;
}

export const TagsInput: FC<TagsInputProps> = ({
    name,
    placeholder,
    separators = DEFAULT_SEPARATORS,
    size,
    className,
}) => {
    const [valueField, , valueHelpers] = useField<string[]>(name);
    const newValueInput = useRef<HTMLInputElement>(null);

    const addNewValue = useCallback(
        (value: string | string[]) => {
            const currentValue = valueField.value ?? [];
            const newValues = (Array.isArray(value) ? value : [value]).filter(
                Boolean,
            );

            const setValuePromise: Promise<unknown> = valueHelpers.setValue([
                ...new Set([...currentValue, ...newValues]),
            ]);

            if (newValueInput.current) {
                newValueInput.current.value = "";
            }

            return setValuePromise;
        },
        [valueField.value, valueHelpers],
    );

    const removeValue = useCallback(
        (value: string) => {
            valueHelpers.setValue(valueField.value?.filter((v) => v !== value));
        },
        [valueField.value, valueHelpers],
    );

    const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (separators.includes(e.key)) {
            addNewValue(newValueInput.current?.value.trim() ?? "");
            e.preventDefault();
        }
    };

    const handlePaste = async (e: React.ClipboardEvent<HTMLInputElement>) => {
        const pastedText = e.clipboardData.getData("text");
        const values = pastedText
            .split(/[\s,]+/) // Split by whitespace or comma
            .map((value) => value.trim())
            .filter((value) => value); // Remove empty values

        await addNewValue(values);
        newValueInput.current!.value = ""; // Clear input after pasting
        e.preventDefault();
    };

    const onBlur = async () => {
        await addNewValue(newValueInput.current?.value.trim() ?? "");
        await valueHelpers.setTouched(true);
    };

    const focusInputInteraction = useInteraction(() =>
        newValueInput.current?.focus(),
    );

    return (
        <div
            className={classNames(
                "form-control",
                `form-control-${size}`,
                styles.tagsInput,
                className,
            )}
            {...focusInputInteraction}
        >
            {valueField.value?.map((item) => (
                <Tag key={item}>
                    {item}
                    <Button
                        variant="tertiary"
                        icon
                        onClick={() => removeValue(item)}
                        size="sm"
                        className={styles.removeBtn}
                    >
                        <CloseIcon />
                    </Button>
                </Tag>
            ))}
            <input
                onBlur={() => onBlur()}
                onKeyDown={onKeyDown}
                onPaste={handlePaste}
                ref={newValueInput}
                placeholder={placeholder ?? ""}
            />
        </div>
    );
};
